import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { ProductImage } from 'app/common/Common'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  DcButton,
  DcFlexBox,
  PixelFactoryContext,
  PixelFlexBox,
  PixelInput,
  PixelPageContainer,
  PixelTable,
  PixelTooltip,
  Section,
  SectionContent,
  SectionTitle,
  colors,
} from 'pixel-base'
import { useApi } from 'shared/hooks'
import styled from 'styled-components'
import {
  getPurchaseDetails,
  updatePurchaseStatus,
  updateReceiveCount,
} from 'utils/api-routes.util'

import PurchaseSummary from './components/PurchaseSummary'
import { IPixelFactory } from '../../../../../../decl.d'

const PurchaseDetail = ({ purchaseId }: { purchaseId: string }) => {
  const { state } = React.useContext(OrdersContext)
  const navigate = useNavigate()
  const [warehouseList, setWarehouseList] = React.useState<any[]>([])
  React.useEffect(() => {
    setWarehouseList(state?.warehouses || [])
  }, [state?.warehouses])

  const {
    pixelConfirm,
    showSuccess,
    showFailure,
    showPageLoader,
    hidePageLoader,
    showLoader,
    hideLoader,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [purchase, setPurchase] = React.useState<any>({})
  const [cart, setCart] = React.useState([])

  // Use useApi hook for fetching purchase details
  const { loading } = useApi(() => getPurchaseDetails(purchaseId), {
    onSuccess: (data: any) => {
      setPurchase(data?.data || {})
      setCart(data?.data?.purchase_items || [])
      hideLoader()
    },
    onError: (error) => {
      console.error('Failed to fetch purchase details:', error)
      hideLoader()
    },
    skip: !purchaseId,
    deps: [purchaseId],
  })

  // Show loader when starting to fetch data
  React.useEffect(() => {
    if (purchaseId && !loading) {
      showLoader()
    }
  }, [purchaseId, loading, showLoader])

  const updatePurchasesStatus = (purchaseId, status) => {
    pixelConfirm({
      title: 'Confirmation',
      message: `Marking Complete will update the stock counts in the warehouse ${currentWarehouse()}`,
      onConfirm: () => {
        showPageLoader()
        updatePurchaseStatus(purchaseId, status).subscribe((res: any) => {
          hidePageLoader()
          if (res.response.data) {
            showSuccess('Purchase status updated successfully')
            navigate(`/inventory/purchases/detail/${purchaseId}`)
            // Trigger refetch of purchase details
            // This will be handled automatically by the useApi hook when purchaseId changes
          } else {
            showFailure(res.response.error)
          }
        })
      },
    })
  }

  useEffect(() => {
    // fetchPurchaseDetails(purchaseId) // This line is no longer needed as useApi handles the fetch
  }, [purchaseId])

  const updateRowReceivedCount = (value, row) => {
    const InputValue = value === 0 ? 0 : value.replace(/^0+/, '')
    const updatedCart: any = cart.map((item: any) => {
      const value = item.quantity - item.damaged_count
      if (item.purchase_item_id === row.purchase_item_id) {
        item.accepted_count =
          item.quantity === item.damaged_count
            ? 0
            : InputValue <= value && InputValue
      }
      return item
    })
    setCart(updatedCart)
  }
  const updateRowDamagedCount = (value, row) => {
    const InputValue = value === 0 ? 0 : value.replace(/^0+/, '')
    const updatedCart: any = cart.map((item: any) => {
      const value = item.quantity - item.accepted_count

      if (item.purchase_item_id === row.purchase_item_id) {
        item.damaged_count =
          item.quantity === item.accepted_count
            ? 0
            : InputValue <= value && InputValue
      }
      return item
    })
    setCart(updatedCart)
  }
  const AcceptedAll = () => {
    const totalCart: any = cart?.map((item: any) => {
      return {
        ...item,
        accepted_count: Number(item.quantity),
        damaged_count: '0',
      }
    })
    setCart(totalCart)
  }
  const updateReceivedCount = (row) => {
    const updatedCount: any = cart?.map((item: any) => {
      if (item.purchase_item_id === row.purchase_item_id) {
        item.accepted_count = Number(item.quantity)
        item.damaged_count = '0'
      }
      return item
    })
    setCart(updatedCount)
  }
  const updateDamagedCount = (row) => {
    const updatedCount: any = cart?.map((item: any) => {
      if (item.purchase_item_id === row.purchase_item_id) {
        item.damaged_count = Number(item.quantity)
        item.accepted_count = '0'
      }
      return item
    })
    setCart(updatedCount)
  }
  const updatePurchase = (purchaseId) => {
    const data = {
      received: cart?.map((item: any) => {
        return {
          purchase_item_id: item.purchase_item_id,
          accepted_count: Number(item.accepted_count),
          damaged_count: Number(item.damaged_count),
        }
      }),
    }
    showPageLoader()
    updateReceiveCount(data, purchaseId).subscribe((res: any) => {
      hidePageLoader()
      if (res.response.data) {
        showSuccess('Purchase updated successfully')
        setPurchase(res.response.data)
        setCart(res.response.data?.cart)
      } else {
        showFailure(res.response.error)
      }
    })
  }
  const totalCount = (accepted?, damaged?, defaultt?, total?) => {
    const receivedCount = (Number(accepted) / Number(total)) * 100
    const damagedCount = Number(damaged / Number(total)) * 100
    const defaultCount = Number(defaultt / Number(total)) * 100
    return {
      acceptedPer: receivedCount,
      damagedPer: damagedCount,
      defaultPer: defaultCount,
    }
  }
  const remainingCount = (total, accepted, damaged) => {
    return Number(total) - (Number(accepted) + Number(damaged))
  }
  const currentWarehouse = () => {
    if (warehouseList.length > 0) {
      return warehouseList.find((item) => item.wh_id === purchase.warehouse_id)
        .name
    }
  }
  const column = [
    {
      dataKey: 'product_image_path',
      name: 'Image',
      width: '8%',
      selector: (row) => (
        <>
          <ProductImage
            style={{
              width: '30%',
              marginRight: '5px',
              maxHeight: '450px',
            }}
            path={row.product?.product_image_path}
          />
        </>
      ),
    },
    {
      dataKey: 'purchase_invoice_id',
      name: 'Invoice',
      sortable: true,
      selector: (row) => row.purchase_invoice_id || '--',
    },
    {
      dataKey: 'sku',
      name: 'SKU',
      sortable: true,
      selector: (row) => row.product?.product_sku || '--',
    },
    {
      dataKey: 'purchase_price',
      name: 'Unit Cost',
      sortable: true,
      selector: (row) =>
        row.product?.purchase_price || row.cost_unit_price || '--',
    },
    {
      dataKey: 'quantity',
      name: 'Purchased',
      sortable: true,
      selector: (row) => row.received_count || '--',
    },
    {
      dataKey: 'received_count',
      name: 'Received',
      sortable: true,
      width: '15%',
      selector: (row) => (
        <CountBar>
          <ReceivedCount
            tooltipText={`Accepted ${row.accepted_count} of ${row.quantity}`}
            width={`${
              totalCount(
                row.accepted_count,
                row.damaged_count,
                remainingCount(
                  row.quantity,
                  row.accepted_count,
                  row.damaged_count,
                ),
                row.quantity,
              ).acceptedPer
            }%`}
            background={colors.success}
          ></ReceivedCount>
          <ReceivedCount
            tooltipText={`Damaged ${row.damaged_count} of ${row.quantity}`}
            width={`${
              totalCount(
                row.accepted_count,
                row.damaged_count,
                remainingCount(
                  row.quantity,
                  row.accepted_count,
                  row.damaged_count,
                ),
                row.quantity,
              ).damagedPer
            }%`}
            background={colors.danger}
          ></ReceivedCount>
          <ReceivedCount
            tooltipText={`No Received ${
              Number(row.quantity) -
              (Number(row.accepted_count) + Number(row.damaged_count))
            } of ${row.quantity}`}
            width={`${
              totalCount(
                row.accepted_count,
                row.damaged_count,
                remainingCount(
                  row.quantity,
                  row.accepted_count,
                  row.damaged_count,
                ),
                row.quantity,
              ).defaultPer
            }%`}
            background={colors.grayBorderLight}
          ></ReceivedCount>
        </CountBar>
      ),
    },
    {
      dataKey: 'accepted',
      name: 'Accepted',
      sortable: true,
      width: '15%',
      selector: (row) => (
        <PixelFlexBox>
          <PixelInput
            noPadding
            required
            min="0"
            height="32px"
            type={'number'}
            name={'accepted'}
            placeholder={''}
            value={row.accepted_count || 0}
            onChange={(e) => {
              const value = e.target.value === '' ? 0 : e.target.value
              if (Number(value) <= Number(row.quantity)) {
                updateRowReceivedCount(value, row)
              }
            }}
          />
          <DcButton
            size="sm"
            variant="outline"
            onClick={() => {
              updateReceivedCount(row)
            }}
          >
            All
          </DcButton>
        </PixelFlexBox>
      ),
    },
    {
      dataKey: 'damaged',
      name: 'Damaged',
      sortable: true,
      width: '15%',

      selector: (row) => (
        <PixelFlexBox>
          <PixelInput
            noPadding
            height="32px"
            required
            type={'number'}
            name={'damaged'}
            min="0"
            placeholder={''}
            value={row.damaged_count || 0}
            onChange={(e) => {
              const value = e.target.value === '' ? 0 : e.target.value
              if (Number(value) <= Number(row.quantity)) {
                updateRowDamagedCount(value, row)
              }
            }}
          />
          <DcButton
            size="sm"
            variant="outline"
            onClick={() => {
              updateDamagedCount(row)
            }}
          >
            All
          </DcButton>
        </PixelFlexBox>
      ),
    },
  ]
  const completedColumn = [
    {
      dataKey: 'product_image_path',
      name: 'Image',
      width: '8%',
      selector: (row) => (
        <>
          <ProductImage
            style={{
              width: '32px',
              marginRight: '5px',
            }}
            path={row.product?.product_image_path}
          />
        </>
      ),
    },

    {
      dataKey: 'sku',
      name: 'SKU',
      sortable: true,
      selector: (row) => row.product?.product_sku || '--',
    },
    {
      dataKey: 'received_count',
      name: 'Received',
      sortable: true,
      selector: (row) => row.received_count || '--',
    },
    {
      dataKey: 'accepted_count',
      name: 'Accepted',
      sortable: true,
      selector: (row) => row.accepted_count || '--',
    },
    {
      dataKey: 'damaged_count',
      name: 'Damaged',
      sortable: true,
      selector: (row) => row.damaged_count || '--',
    },
    {
      dataKey: 'purchase_price',
      name: 'Unit Price',
      sortable: true,

      selector: (row) => row?.purchase_price || row?.cost_unit_price || '--',
    },
  ]

  return (
    <>
      <PixelPageContainer padding="0px">
        {purchase?.purchase_status?.status === 'Received' && (
          <DcFlexBox
            height="auto"
            padding="5px 20px"
            gap="5px"
            justifyContent="flex-end"
          >
            <DcButton
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                AcceptedAll()
              }}
            >
              Mark All Accepted
            </DcButton>
            <DcButton
              size="sm"
              onClick={() => {
                updatePurchase(purchaseId)
              }}
            >
              Update
            </DcButton>
            <DcButton
              size="sm"
              onClick={() => {
                updatePurchasesStatus(purchaseId, { status: 'completed' })
              }}
            >
              Mark As Complete
            </DcButton>
          </DcFlexBox>
        )}
        <Section style={{ padding: '20px 20px 0px 20px' }}>
          <PurchaseSummary purchaseSummary={purchase} />
        </Section>
        <Section style={{ padding: '20px' }}>
          <SectionContent className="mt-2">
            <SectionTitle style={{ padding: '30px 0px 0px 20px' }}>
              Products
            </SectionTitle>
            {purchase?.purchase_status?.status === 'Received' ? (
              <>
                <PixelTable
                  columns={column}
                  data={cart}
                  pointerOnHover={true}
                  progressPending={loading}
                  pagination={cart.length > 25 ? true : false}
                  fixedHeader={true}
                  fixedHeaderScrollHeight="calc(100vh - 500px)"
                  highlightOnHover={true}
                />
              </>
            ) : (
              <>
                <PixelTable
                  columns={completedColumn}
                  data={cart}
                  pointerOnHover={true}
                  progressPending={loading}
                  pagination={cart.length > 25 ? true : false}
                  fixedHeader={true}
                  fixedHeaderScrollHeight="calc(100vh - 500px)"
                  highlightOnHover={true}
                />
              </>
            )}
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </>
  )
}

const CountBar = styled.div`
  width: 100px;
  height: 25px;
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;
  align-content: center;
  height: 20px;
  border-radius: 5px;
  overflow: none;
`
const ReceivedCount = styled(PixelTooltip)<{
  width: string
  background: string
}>`
  width: ${(props) => props.width && props.width};
  height: 90%;
  background: ${(props) => props.background && props.background};
  -webkit-transition: width 0.5s 0s linear;
  -moz-transition: width 0.5s 0s linear;
  -o-transition: width 0.5s 0s linear;
  transition: width 0.5s 0s linear;
`

export default PurchaseDetail
